import { ICognitoStorage } from 'amazon-cognito-identity-js';

const REMEMBER_ME_KEY = 'c360:rememberMe';
const COGNITO_PREFIX = 'CognitoIdentityServiceProvider.';

/**
 * Custom Cognito storage adapter that delegates to localStorage or sessionStorage
 * based on the user's "remember me" preference. This is a singleton — one instance
 * is shared across the entire app via the CognitoUserPool `Storage` option.
 */
class CognitoStorageAdapter implements ICognitoStorage {
  private backingStore: Storage;

  constructor() {
    const saved = localStorage.getItem(REMEMBER_ME_KEY);
    this.backingStore = saved === 'false' ? sessionStorage : localStorage;
  }

  setRememberMe(value: boolean): void {
    localStorage.setItem(REMEMBER_ME_KEY, String(value));
    this.backingStore = value ? localStorage : sessionStorage;
  }

  getRememberMe(): boolean {
    return localStorage.getItem(REMEMBER_ME_KEY) !== 'false';
  }

  getItem(key: string): string | null {
    return this.backingStore.getItem(key);
  }

  setItem(key: string, value: string): void {
    this.backingStore.setItem(key, value);
  }

  removeItem(key: string): void {
    // Remove from BOTH storages to prevent stale token leaks
    // when switching remember-me preference
    localStorage.removeItem(key);
    sessionStorage.removeItem(key);
  }

  clear(): void {
    // Only remove CognitoIdentityServiceProvider.* keys — protect known accounts registry
    // and other app data stored in localStorage/sessionStorage
    const removeMatchingKeys = (storage: Storage) => {
      const keysToRemove: string[] = [];
      for (let i = 0; i < storage.length; i++) {
        const key = storage.key(i);
        if (key?.startsWith(COGNITO_PREFIX)) {
          keysToRemove.push(key);
        }
      }
      keysToRemove.forEach((key) => storage.removeItem(key));
    };

    removeMatchingKeys(localStorage);
    removeMatchingKeys(sessionStorage);
  }
}

const cognitoStorageAdapter = new CognitoStorageAdapter();

export default cognitoStorageAdapter;
